/**
 * Quote-summary PDF export + custom-quote email helpers.
 *
 * Extracted from `QuoteCalculator.jsx` (was inline ~100 lines) so the
 * main wizard component can focus on render/state orchestration. These
 * functions are **pure** — they take the current quote state + a
 * `formatCurrency` helper and side-effect to either a downloaded PDF or
 * a `mailto:` window redirect. No reactive state, no refs.
 */
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { productData } from '../../data/mockData';

const BRAND_COLOR_RGB = [74, 124, 89]; // #4A7C59
const CONTACT_EMAIL = 'jfishburn@eg3.ca';
const CONTACT_PHONE = '(905) 447-0257';

/**
 * Build + download a branded PDF quote summary.
 *
 * @param {Object} params — all primitive quote state the PDF embeds
 * @param {Function} formatCurrency — currency formatter from the caller
 */
export const exportQuotePdf = ({
  selectedProduct,
  selectedFormat,
  selectedMaterial,
  selectedShape,
  width,
  height,
  unit,
  quantity,
  shippingMethod,
  shippingTimes,
  country,
  availableMaterials,
  subtotal,
  shipping,
  total,
  formatCurrency,
}) => {
  const doc = new jsPDF();

  doc.setFontSize(20);
  doc.setTextColor(...BRAND_COLOR_RGB);
  doc.text('Evergreen Graphics Group Inc', 20, 20);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text('Professional Print Solutions', 20, 28);
  doc.text(`Phone: ${CONTACT_PHONE}`, 20, 34);
  doc.text(`Email: ${CONTACT_EMAIL}`, 20, 40);

  doc.setFontSize(16);
  doc.setTextColor(0);
  doc.text('Quote Summary', 20, 55);

  const category = productData.categories.find((c) => c.id === selectedProduct);
  const subcategory = category?.subcategories.find((s) => s.id === selectedFormat);
  const material = (availableMaterials || []).find((m) => m.id === selectedMaterial);

  const quoteDetails = [
    ['Product Category', category?.name || 'N/A'],
    ['Product Format', subcategory?.name || 'N/A'],
    ['Material/Finish', material?.name || 'N/A'],
    ['Shape', selectedShape === 'die-cut' ? 'Die-Cut' : 'Standard Shapes'],
    ['Dimensions', `${width} x ${height} ${unit}`],
    ['Quantity', quantity?.toLocaleString() || 'N/A'],
    ['Shipping Method', shippingMethod.charAt(0).toUpperCase() + shippingMethod.slice(1)],
    ['Shipping Time', shippingTimes[shippingMethod]],
    ['Destination', country],
  ];

  doc.autoTable({
    startY: 65,
    head: [['Item', 'Details']],
    body: quoteDetails,
    theme: 'striped',
    headStyles: { fillColor: BRAND_COLOR_RGB },
    margin: { left: 20, right: 20 },
  });

  const finalY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.text('Pricing Breakdown', 20, finalY);

  const pricingData = [
    ['Subtotal', formatCurrency(subtotal)],
    ['Shipping & Handling', formatCurrency(shipping)],
    ['Total (before taxes)', formatCurrency(parseFloat(total))],
  ];

  doc.autoTable({
    startY: finalY + 5,
    body: pricingData,
    theme: 'plain',
    margin: { left: 20, right: 20 },
    styles: { fontSize: 12 },
    columnStyles: {
      0: { fontStyle: 'bold' },
      1: { halign: 'right', fontStyle: 'bold' },
    },
  });

  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text('This quote is valid for 30 days from the date of issue.', 20, pageHeight - 20);
  doc.text('Prices do not include applicable taxes.', 20, pageHeight - 15);
  doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, pageHeight - 10);

  doc.save(`Evergreen_Quote_${Date.now()}.pdf`);
};

/**
 * Determine if the customer's CURRENT product+format combo prices
 * per-page (sheet) or per-individual-item.
 *
 * iter114s.8 — bug fix: previously read `category.pricingType` which
 * **doesn't exist** in the data. Only formats define `quantityType`
 * (`'page'` or `'individual'`). Default-to-`'pages'` therefore made
 * EVERY product (magnets, decals, transparent stickers, kiss-cut…)
 * mis-render as page-mode in the Quick Quote panel — labels said
 * "Pages: 50" + "$X / page" + the alt-size identical-price helper
 * line, when the customer was actually buying 50 individual items at
 * $X each.
 *
 * Fix: walk to the FORMAT level and read its `quantityType`. Default
 * to `'individual'` (the more conservative choice) so any format that
 * forgets to set the field still prices correctly per-unit. The only
 * page-mode formats we actually ship are the ones explicitly tagged
 * `quantityType: 'page'` in `mockData.js` — page sheet vinyl, page
 * sheet permanent stickers, etc. Everything else is individual.
 *
 * Signature accepts BOTH `selectedProduct` (category id) and
 * `selectedFormat` (format id). Old single-arg callers still work
 * (they default to `selectedFormat === undefined` → individual).
 */
export const resolvePricingType = (selectedProduct, selectedFormat) => {
  if (!selectedProduct || !selectedFormat) return 'individual';
  const cat = productData.categories?.find((c) => c.id === selectedProduct);
  if (!cat) return 'individual';
  const fmt = (cat.formats || cat.subcategories || []).find(
    (f) => f.id === selectedFormat,
  );
  if (!fmt) return 'individual';
  return fmt.quantityType === 'page' ? 'pages' : 'individual';
};
